import React from 'react';
import { theme } from '@/theme';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface TileProps {
  /** Main content of the tile */
  children?: React.ReactNode;
  
  /** Title text */
  title?: string;
  
  /** Description or subtitle */
  description?: string;
  
  /** Optional icon component */
  icon?: LucideIcon;
  
  /** Icon color override */
  iconColor?: string;
  
  /** Footer content */
  footer?: React.ReactNode;
  
  /** Visual variant */
  variant?: 'default' | 'glass' | 'elevated' | 'outline' | 'gradient' | 'flat';
  
  /** Size variant */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  
  /** Enable hover effects */
  hoverable?: boolean;
  
  /** Make tile clickable */
  onClick?: () => void;
  
  /** Link href (makes tile a link) */
  href?: string;
  
  /** Additional CSS classes */
  className?: string;
  
  /** Custom background color or gradient */
  background?: string;
  
  /** Image source for tile background or top section */
  image?: string;
  
  /** Image position */
  imagePosition?: 'background' | 'top' | 'left';
  
  /** Enable spotlight effect on hover */
  spotlight?: boolean;
  
  /** Badge text (top right corner) */
  badge?: string;
  
  /** Disable default padding */
  noPadding?: boolean;
}

const Tile: React.FC<TileProps> = ({
  children,
  title,
  description,
  icon: Icon,
  iconColor = theme.colors.accent,
  footer,
  variant = 'default',
  size = 'md',
  hoverable = false,
  onClick,
  href,
  className,
  background,
  image,
  imagePosition = 'top',
  spotlight = false,
  badge,
  noPadding = false,
}) => {
  const Component = href ? 'a' : onClick ? 'button' : 'div';
  
  // Size mappings
  const sizeClasses = {
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
    xl: 'p-10',
  };
  
  // Variant styles
  const variantStyles: Record<string, React.CSSProperties> = {
    default: {
      backgroundColor: theme.colors.bgElevated,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.md,
    },
    glass: {
      background: 'rgba(10, 10, 10, 0.6)',
      backdropFilter: 'blur(20px)',
      border: `1px solid rgba(255, 255, 255, 0.1)`,
      boxShadow: `${theme.shadows.lg}, inset 0 0 20px rgba(255, 255, 255, 0.05)`,
    },
    elevated: {
      backgroundColor: theme.colors.bgElevated,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.lg,
      transform: 'translateY(-2px)',
    },
    outline: {
      backgroundColor: 'transparent',
      border: `2px solid ${theme.colors.border}`,
    },
    gradient: {
      background: theme.gradients.warm,
      border: `1px solid ${theme.colors.border}`,
      boxShadow: theme.shadows.md,
    },
    flat: {
      backgroundColor: theme.colors.bgElevated,
    },
  };
  
  // Hover effects
  const hoverStyles: React.CSSProperties = hoverable || onClick || href ? {
    transition: theme.transitions.normal,
    cursor: 'pointer',
  } : {};
  
  const baseStyles: React.CSSProperties = {
    position: 'relative',
    borderRadius: theme.borderRadius.xl,
    overflow: 'hidden',
    ...variantStyles[variant],
    ...hoverStyles,
    ...(background ? { background } : {}),
  };
  
  const contentWrapperStyles: React.CSSProperties = {
    position: 'relative',
    zIndex: 1,
  };
  
  // Image background overlay
  const imageBackgroundStyles: React.CSSProperties = image && imagePosition === 'background' ? {
    position: 'absolute',
    inset: 0,
    backgroundImage: `url(${image})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    opacity: 0.3,
    zIndex: 0,
  } : {};
  
  return (
    <Component
      onClick={onClick}
      href={href}
      className={cn(
        'tile-component relative',
        !noPadding && sizeClasses[size],
        hoverable && 'hover:shadow-2xl hover:border-accent/50 hover:-translate-y-1',
        onClick && 'focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-2 focus:ring-offset-background',
        className
      )}
      style={baseStyles}
      onMouseEnter={(e) => {
        if (hoverable || onClick || href) {
          const target = e.currentTarget as HTMLElement;
          target.style.borderColor = `${theme.colors.accent}50`;
          if (variant === 'glass') {
            target.style.background = 'rgba(10, 10, 10, 0.8)';
          } else if (variant !== 'gradient') {
            target.style.backgroundColor = theme.colors.bgHover;
          }
          if (spotlight) {
            target.style.boxShadow = theme.shadows.accentHover;
          }
        }
      }}
      onMouseLeave={(e) => {
        if (hoverable || onClick || href) {
          const target = e.currentTarget as HTMLElement;
          target.style.borderColor = variant === 'outline' ? theme.colors.border : (variantStyles[variant].border as string)?.split(' ')[2] || theme.colors.border;
          if (variant === 'glass') {
            target.style.background = 'rgba(10, 10, 10, 0.6)';
          } else if (variant !== 'gradient') {
            target.style.backgroundColor = variantStyles[variant].backgroundColor as string;
          }
          target.style.boxShadow = variantStyles[variant].boxShadow as string || 'none';
        }
      }}
    >
      {/* Background image */}
      {image && imagePosition === 'background' && (
        <div style={imageBackgroundStyles} />
      )}
      
      {/* Badge */}
      {badge && (
        <div
          className="absolute top-4 right-4 z-10 px-3 py-1 text-xs font-semibold rounded-full"
          style={{
            backgroundColor: theme.colors.accent,
            color: theme.colors.text,
          }}
        >
          {badge}
        </div>
      )}
      
      <div style={contentWrapperStyles}>
        {/* Top Image */}
        {image && imagePosition === 'top' && (
          <div className={cn('w-full rounded-lg overflow-hidden', !noPadding ? 'mb-4' : '')}>
            <img 
              src={image} 
              alt={title || 'Tile image'} 
              className="w-full h-48 object-cover"
            />
          </div>
        )}
        
        {/* Left Image Layout */}
        {image && imagePosition === 'left' ? (
          <div className="flex gap-4">
            <div className="flex-shrink-0">
              <img 
                src={image} 
                alt={title || 'Tile image'} 
                className="w-24 h-24 object-cover rounded-lg"
              />
            </div>
            <div className="flex-1">
              {renderContent()}
            </div>
          </div>
        ) : (
          renderContent()
        )}
      </div>
    </Component>
  );
  
  function renderContent() {
    return (
      <>
        {/* Icon */}
        {Icon && (
          <div className="mb-4">
            <Icon 
              size={size === 'sm' ? 24 : size === 'md' ? 32 : size === 'lg' ? 40 : 48} 
              style={{ color: iconColor }}
            />
          </div>
        )}
        
        {/* Title */}
        {title && (
          <h3 
            className={cn(
              'font-bold mb-2',
              size === 'sm' && 'text-lg',
              size === 'md' && 'text-xl',
              size === 'lg' && 'text-2xl',
              size === 'xl' && 'text-3xl'
            )}
            style={{ color: theme.colors.text }}
          >
            {title}
          </h3>
        )}
        
        {/* Description */}
        {description && (
          <p 
            className={cn(
              'mb-4',
              size === 'sm' && 'text-sm',
              size === 'md' && 'text-base',
              size === 'lg' && 'text-lg',
              size === 'xl' && 'text-xl'
            )}
            style={{ color: theme.colors.textMuted }}
          >
            {description}
          </p>
        )}
        
        {/* Children */}
        {children}
        
        {/* Footer */}
        {footer && (
          <div className="mt-4 pt-4 border-t" style={{ borderColor: theme.colors.border }}>
            {footer}
          </div>
        )}
      </>
    );
  }
};

export default Tile;

